import Common from './Common';
import Storage from '../utils/Storage';
import Group from '../models/Group';
import { heFetch } from './HeFetch';
import cache from '../utils/PromiseCache';

export default class User {
    static getUserId() {
        return window?.gtg?.userId ? window.gtg.userId : null;
    }

    static identity() {
        return Storage.get('user', 'session');
    }

    static me(noCache) {
        if (!noCache && Storage.get('user', 'session')) {
            const user = Storage.get('user', 'session');
            const deferred = $.Deferred();
            deferred.resolve(user);
            return deferred;
        }
        const params = {
            url: '/v1/users/me',
            method: 'get'
        };

        if (noCache) {
            params.cache = false;
        }

        const promise = Common.request(params);
        promise.then((user) => {
            Storage.set('user', user, 'session');
        });
        return promise;
    }

    static get(filters, orderBy, limit, offset) {
        const params = Common.params(
            {
                url: '/v1/users',
                method: 'get'
            },
            filters,
            orderBy,
            limit,
            offset
        );
        return Common.request(params);
    }

    static getOne(userId, { noCache = false, ttlMs = 20000 } = {}) {
        if (!userId) {
            return Promise.resolve(undefined);
        }

        const key = `User.getOne:${userId}`;
        const fetcher = () => Common.request(Common.params({ url: `/v1/users/${userId}`, method: 'get' }));

        return cache.getTransformed(key, { ttlMs, noCache }, fetcher, (x) => x);
    }

    static getUserJWTToken() {
        const params = Common.params({
            url: '/v1/marketing/token',
            method: 'get'
        });
        return Common.request(params);
    }

    static getLastVerified(offset, limit) {
        const params = Common.params(
            {
                url: '/v1/users/get-last-verified',
                method: 'get',
                data: {
                    filters: {
                        completionRate: 100
                    }
                }
            },
            null,
            null,
            limit,
            offset
        );
        return Common.request(params);
    }

    static patch(userId, data, options) {
        // compute birth_date fucking input format
        if (typeof data.profil === 'object' && data.profil.birth_date) {
            const birthdate = data.profil.birth_date;
            data.profil.birth_date = typeof birthdate === 'number' ? birthdate : moment(birthdate).year();
        }

        return Common.request(
            Object.assign(
                {
                    url: `/v1/users/${userId}`,
                    method: 'patch',
                    data
                },
                options
            )
        ).done((user) => {
            Storage.set('user', user, 'session');
        });
    }

    static postImage(image, callback, fail) {
        const formData = new FormData();
        formData.append('file', image);
        const params = {
            url: '/v1/images/user',
            method: 'post',
            data: formData,
            contentType: false,
            processData: false,
            progressBarProfile: true
        };
        return Common.fetch(params, callback, fail);
    }

    static fetchGroups() {
        const params = Common.params({
            url: '/v3/groups',
            method: 'get'
        });

        return Common.request(params)
            .then((response) => {
                if (response.data) {
                    return response.data.groups;
                } else {
                    return response;
                }
            })
            .then((groups) => {
                Storage.set('groups', groups);
                return groups;
            });
    }

    static getGroup(groupId, limit = 15, offset = 0) {
        const params = Common.params(
            {
                url: `/v1/groups/view/${groupId}`,
                method: 'get'
            },
            null,
            null,
            limit,
            offset
        );
        return Common.request(params);
    }

    static getGroups() {
        const deferred = $.Deferred();

        let groups = [];
        if (Storage.get('groups')) {
            groups = Storage.get('groups').map((group) => new Group(group));
            deferred.resolve(groups);
        } else {
            User.fetchGroups().done((grps) => {
                grps = grps || [];
                grps = grps.map((group) => new Group(group));
                deferred.resolve(grps);
            });
        }

        return deferred.promise();
    }

    static joinGroup(id) {
        const params = Common.params({
            url: `/v1/groups/join`,
            method: 'post',
            data: {
                group_id: id
            }
        });
        return Common.request(params);
    }

    static leaveGroup(id) {
        const params = Common.params({
            url: `/v1/groups/leave`,
            method: 'patch',
            data: {
                group_id: id
            }
        });
        return Common.request(params);
    }

    static fetchFriends(offset, limit) {
        const params = Common.params(
            {
                url: `/v1/friends`,
                method: 'get'
            },
            null,
            null,
            limit,
            offset
        );
        return Common.request(params);
    }

    static fetchPendingFriends(offset, limit) {
        const params = Common.params(
            {
                url: `/v1/friends/pending`,
                method: 'get'
            },
            null,
            null,
            limit,
            offset
        );
        return Common.request(params);
    }

    static fetchSentFriendRequests(offset, limit) {
        const params = Common.params(
            {
                url: `/v1/friends/requests`,
                method: 'get'
            },
            null,
            null,
            limit,
            offset
        );
        return Common.request(params);
    }

    static acceptFriend(userId) {
        const params = Common.params({
            url: `/v1/friends/${userId}/accept`,
            method: 'patch'
        });
        return Common.request(params);
    }

    static refuseFriend(userId) {
        const params = Common.params({
            url: `/v1/friends/${userId}/refuse`,
            method: 'patch'
        });
        return Common.request(params);
    }

    static addFriend(userId) {
        const params = Common.params({
            url: `/v1/friends/${userId}`,
            method: 'post'
        });
        return Common.request(params);
    }

    static deleteFriend(userId) {
        const params = Common.params({
            url: `/v1/friends/${userId}`,
            method: 'delete'
        });
        return Common.request(params);
    }

    static getFriends() {
        return Storage.get('userFriends');
    }

    static fetchUserGroups(userId) {
        const params = Common.params({
            url: '/v3/groups',
            method: 'get',
            data: {
                user_id: userId
            }
        });

        return Common.request(params)
            .then((response) => {
                if (response.data) {
                    return response.data.groups;
                } else {
                    return response;
                }
            })
            .then((groups) => {
                Storage.set(`${userId}_groups`, groups);
                return groups;
            })
            .then((groups) => groups.map((group) => new Group(group)));
    }

    static getUserGroups(userId) {
        const deferred = $.Deferred();
        let groups = [];

        if (Storage.get(`${userId}_groups`)) {
            groups = Storage.get(`${userId}_groups`);
            groups = groups.map((group) => new Group(group));
            deferred.resolve(groups);
        } else {
            User.fetchUserGroups(userId).done((grps) => {
                if (Array.isArray(grps)) {
                    grps = grps.map((group) => new Group(group));
                    return deferred.resolve(grps);
                }
                deferred.reject('#fetchUserGroups() should return an array');
            });
        }
        return deferred.promise();
    }

    static getContactedMembers() {
        return Storage.get('contactedMembers', 'session');
    }

    static delete(userId) {
        return heFetch.delete(`/users/${userId}`);
    }

    static create(user, isCollection = false) {
        return Common.request({
            url: isCollection ? '/v1/collection/user' : '/v1/users',
            method: 'post',
            data: user,
            auth: false
        });
    }

    static createFromFacebook(accessToken, email) {
        return Common.request({
            url: '/v1/users/facebook',
            method: 'post',
            data: {
                facebook_token: accessToken,
                email
            },
            auth: false
        });
    }

    static generatePhoneVerificationCode(user) {
        return heFetch.post('/phone-verification/send-code', {
            data: { language: user.get('locale') }
        });
    }

    static sendPhoneVerificationCode(code) {
        return heFetch.post('/phone-verification/validate-code', {
            data: { code }
        });
    }

    static getTransferts(filters, orderBy, limit = 10000, offset) {
        const params = Common.params(
            {
                url: '/v1/users/transferts',
                method: 'get'
            },
            filters,
            orderBy,
            limit,
            offset
        );
        return Common.request(params);
    }

    static getTransfertDetails() {
        const params = Common.params({
            url: `/v1/users/transferts-details`,
            method: 'get'
        });
        return Common.request(params);
    }

    static getNotifications() {
        const params = Common.params({
            url: '/v1/notification/headband',
            method: 'get'
        });
        return Common.request(params).then((res) => {
            if (!res) {
                return [];
            } else if (Array.isArray(res)) {
                return res;
            } else {
                return [res];
            }
        });
    }

    static linkFacebook(facebookId, facebookAccessToken) {
        const params = Common.params({
            url: '/v1/users/link-facebook',
            method: 'post',
            data: {
                facebookId,
                facebookAccessToken
            }
        });
        return Common.request(params);
    }

    static requestEmailTokenVerif(id) {
        const params = Common.params({
            url: `/v1/verification/email/${id}`,
            method: 'post',
            data: {
                callbackUrl: '/dashboard'
            }
        });
        return Common.request(params);
    }

    static resendEmailVerif(email) {
        const params = Common.params({
            url: '/v1/verification/email/resend',
            method: 'post',
            data: { email }
        });
        return Common.request(params);
    }

    static sendEmailTokenVerif(token) {
        const params = Common.params({
            url: '/v1/verification/token',
            method: 'patch',
            data: {
                token
            }
        });
        return Common.request(params);
    }

    static getPaymentMethod(accessToken) {
        return heFetch.get('/payment-methods', {
            headers: {
                Authorization: `Bearer ${accessToken}`
            }
        });
    }

    static createPaymentMethod(accessToken, data) {
        const response = heFetch.post('/payment-methods', {
            headers: {
                Authorization: `Bearer ${accessToken}`
            },
            data
        });
        return response;
    }

    static removePaymentMethod(accessToken, paymentMethodId) {
        return heFetch.delete(`/payment-methods/${paymentMethodId}`, {
            headers: {
                Authorization: `Bearer ${accessToken}`
            }
        });
    }

    static getTopMenuNotifications() {
        const params = Common.params({
            url: `/v1/notification/topmenu`,
            method: 'get'
        });
        return Common.request(params);
    }

    static getPromoCode(promoCode, productType) {
        const params = Common.params({
            url: `/v1/coupon?product_type=${productType}`,
            method: 'post',
            data: {
                coupon_name: promoCode
            }
        });
        return Common.request(params);
    }

    static getPromoCodeByType(promoCode) {
        const params = Common.params({
            url: `/v1/coupon`,
            method: 'post',
            data: {
                coupon_type: promoCode
            }
        });
        return Common.request(params);
    }

    static changeDefaultPayment(accessToken, uuid) {
        return heFetch.patch(`/payment-methods/${uuid}`, {
            headers: {
                Authorization: `Bearer ${accessToken}`
            }
        });
    }

    static signupCollectionFlowAbandonned() {
        const params = Common.params({
            url: '/v1/collection/user/abandoned-flow',
            method: 'get'
        });
        return Common.request(params);
    }

    static getTransferAchievements() {
        const params = Common.params({
            url: '/v1/transfer/achievements',
            method: 'get'
        });
        return Common.request(params);
    }

    static translateDescription(userId, sourceLanguage, targetedLanguage) {
        return Common.request({
            url: `/v1/users/${userId}/description/translate`,
            method: 'get',
            data: {
                source_language: sourceLanguage,
                targeted_language: targetedLanguage
            }
        });
    }

    static deleteCollectionApplications() {
        const params = Common.params({
            url: '/v1/collection/applications',
            method: 'delete'
        });
        return Common.request(params);
    }
}

export function getUserFromBFF(userId) {
    return heFetch.get(`/users/${userId}`);
}
